import { Label } from '@radix-ui/react-label';
import { Checkbox, Flex, Table } from '@radix-ui/themes';
import { ActionsHeaderCell } from 'components/common/table/actions-cells';
import { TableContext } from 'components/common/table/context';
import { TableHeaderText } from 'components/common/table/header-text';
import { CheckedContext } from 'contexts/layout/checked.context';
import { ACTIONS_KEY, CHECKBOX_KEY } from 'enums/tables';
import { IHeaderCell } from 'interfaces/tables/header';
import { StringHelper } from 'lib_ts/classes/string.helper';
import { useContext, useMemo } from 'react';

const CheckboxCell = (props: IHeaderCell) => {
  const classes = useMemo(
    () =>
      StringHelper.classNames([
        'width-40px',
        props.col.thClassNameFn?.(props.col),
      ]),
    [props.col]
  );

  const { id, tally, setChecked, resetChecked } = useContext(CheckedContext);

  const checkboxID = `cb-${id}-header`;

  return (
    <Table.ColumnHeaderCell
      className={classes}
      data-testid="col-checkbox"
      data-mode={tally.checkAllState}
      align={props.col.align ?? 'center'}
    >
      <Flex align="center" style={{ height: '100%' }}>
        <Label
          htmlFor={checkboxID}
          className="CheckboxWrapperLabel"
          style={{ marginTop: '2px' }}
        >
          <Checkbox
            id={checkboxID}
            // redraw whenever the tally is updated
            key={tally.key}
            defaultChecked={tally.checkAllState}
            color={props.col.color}
            onCheckedChange={(v) => {
              const isAll = v !== false;

              if (isAll) {
                setChecked(props.checkAllIDs ?? [], true);
              } else {
                resetChecked();
              }

              props.afterCheckAll?.(isAll ? 'all' : 'none');
            }}
          />
        </Label>
      </Flex>
    </Table.ColumnHeaderCell>
  );
};

const UnsortableCell = (props: IHeaderCell) => {
  const classes = StringHelper.classNames([
    props.col.thClassNameFn?.(props.col),
  ]);

  return (
    <Table.ColumnHeaderCell
      title={props.col.labelTooltip}
      className={classes}
      align={props.col.align}
    >
      <TableHeaderText {...props} />
    </Table.ColumnHeaderCell>
  );
};

const BasicCell = (props: IHeaderCell) => {
  const classes = useMemo(
    () =>
      StringHelper.classNames([
        'cursor-pointer',
        props.col.thClassNameFn?.(props.col),
      ]),
    [props.col, props.col.thClassNameFn]
  );

  const tableCx = useContext(TableContext);

  return (
    <Table.ColumnHeaderCell
      title={props.col.labelTooltip}
      data-type={props.col.dataType}
      className={classes}
      align={props.col.align}
      onClick={async () => {
        if (!props.enableSort) {
          return;
        }

        if (tableCx.beforeSortFn) {
          await tableCx.beforeSortFn();
        }

        // repeated triggers will cycle through the different dirs automatically
        tableCx.setSort({ key: props.col.key });
      }}
    >
      <TableHeaderText {...props} />
    </Table.ColumnHeaderCell>
  );
};

export const CommonTableHeaderCell = (props: IHeaderCell) => {
  switch (props.col.key) {
    case CHECKBOX_KEY: {
      return <CheckboxCell {...props} />;
    }

    case ACTIONS_KEY: {
      return <ActionsHeaderCell {...props} />;
    }

    default: {
      if (!props.enableSort) {
        // sorting disabled for the whole table
        return <UnsortableCell {...props} />;
      }

      if (props.col.disableSort) {
        // sorting disabled for this column
        return <UnsortableCell {...props} />;
      }

      return <BasicCell {...props} />;
    }
  }
};
